import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Footer from '../Footer/Footer';

const VideoCatalog = ({ videos }) => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const videoRefs = useRef([]);

  // Play/pause videos based on visibility within the scroll container
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(() => {});
            }
          } else {
            video.pause();
          }
        });
      },
      { root: containerRef.current, threshold: 0.4 }
    );

    videoRefs.current.forEach((v) => {
      if (v) {
        v.load();
        observer.observe(v);
      }
    });
    return () => observer.disconnect();
  }, [videos]);

  const handleOpen = (video) => {
    navigate(`/comercial/${video.slug ? video.slug : video.title}`);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100vw',
        height: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollSnapType: 'y proximity',
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        background: '#000',
      }}
    >
      {videos.map((video, index) => (
        <Box
          key={`${video.url}-${index}`}
          onClick={() => handleOpen(video)}
          sx={{
            position: 'relative',
            width: '100vw',
            height: '100vh',
            scrollSnapAlign: 'start',
            cursor: 'pointer',
            overflow: 'hidden',
          }}
        >
          <video
            ref={(el) => (videoRefs.current[index] = el)}
            src={video.url}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />

          <Box
            className="video-details"
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.35) 35%, rgba(0,0,0,0) 60%)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              padding: { xs: '1.25rem', md: '2.5rem' },
              paddingBottom: {
                xs: 'calc(1.25rem + 10vh + env(safe-area-inset-bottom))',
                md: '2.5rem',
              },
              pointerEvents: 'none',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: 'white',
                fontWeight: 600,
                fontSize: { xs: '1.4rem', md: '2rem' },
                mb: 0.5,
              }}
            >
              {video.title}
            </Typography>
            {video.desc && (
              <Typography
                sx={{
                  color: 'white',
                  fontSize: { xs: '0.85rem', md: '1rem' },
                  opacity: 0.9,
                }}
              >
                {video.desc}
              </Typography>
            )}
            {video.director && (
              <Typography
                sx={{
                  color: 'white',
                  fontSize: { xs: '0.8rem', md: '0.95rem' },
                  opacity: 0.85,
                }}
              >
                {video.director}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
      <Box sx={{ scrollSnapAlign: 'end' }}>
        <Footer />
      </Box>
    </Box>
  );
};

export default VideoCatalog;
